import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEvent } from "../../layouts/EventLayout";
import { useSnackbar } from "../../context/SnackbarContext";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { FormPageLayout } from "../../components/forms/FormPageLayout";
import { FormSection } from "../../components/forms/FormSection";
import { useEventParticipants } from "../../hooks/useEventParticipants";
import { useAllPersons } from "../../hooks/useAllPersons";
import { useInviteToEvent } from "../../hooks/useUserManagement";
import {
  buildEligibleOptions,
  findPersonByEmail,
  type MatchedPerson,
} from "./inviteOptions";

/**
 * Add a single attendee to the current event (ADR-073, ADR-092).
 *
 * Mounted at `/events/:eventId/attendees/new` under EventLayout. Replaces
 * the prior `InviteUserDialog`. The page:
 *   1. Surfaces existing users + contacts (not already on the event roster)
 *      via an autocomplete. "On the roster" means an active or pending
 *      `eventParticipants` row for the person (ADR-193); invitation records
 *      are never consulted, so a deleted person's leftover invitation cannot
 *      hide a re-added person. Someone typed in who IS on the roster is
 *      told so, with a link to the Attendees page, instead of being hidden.
 *   2. Submits via `inviteToEvent` to add the picked person to the event.
 *   3. **Sends no email.** Per ADR-092, adding to an event is purely a
 *      data write. Invitation emails are sent later from the Attendees
 *      page Send Emails button.
 *   4. Provides a "Can't find them? Create new user" link that deep-links
 *      to `/users/new` with prefill params.
 */
export function AttendeeCreatePage() {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const { event } = useEvent();
  const { showSuccess, showError } = useSnackbar();

  const { participants } = useEventParticipants(eventId ?? "");
  const { data: allPersons } = useAllPersons();
  const { invite, loading: inviting } = useInviteToEvent();

  const [email, setEmail] = useState(searchParams.get("prefillEmail") ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submittingFlag, setSubmittingFlag] = useState(false);

  // Who is on this event, keyed by personId from their participant row
  // (active or pending). This is the only roster test (ADR-193).
  const rosterStatusByPersonId = useMemo(() => {
    const map = new Map<string, "active" | "pending">();
    for (const p of participants) {
      if (p.status === "active" || p.status === "pending") {
        map.set(p.personId, p.status);
      }
    }
    return map;
  }, [participants]);

  const emailOptions = useMemo(
    () =>
      buildEligibleOptions(allPersons, new Set(rosterStatusByPersonId.keys())),
    [allPersons, rosterStatusByPersonId],
  );

  // Derived match state. The typed email resolves against ALL persons, not
  // just the eligible options, so someone already on the roster is
  // recognised and explained rather than reported as unknown.
  const { match, matchType, rosterStatus } = useMemo<{
    match: MatchedPerson | null;
    matchType: "user" | "contact" | "new";
    rosterStatus: "active" | "pending" | null;
  }>(() => {
    const normalized = email.toLowerCase().trim();
    if (!normalized) return { match: null, matchType: "new", rosterStatus: null };
    const person = findPersonByEmail(allPersons, normalized);
    if (!person) return { match: null, matchType: "new", rosterStatus: null };
    return {
      match: person,
      matchType: person.type,
      rosterStatus: rosterStatusByPersonId.get(person.id) ?? null,
    };
  }, [email, allPersons, rosterStatusByPersonId]);

  const submitting = inviting || submittingFlag;
  const isDirty = !!email;

  const { dialog: unsavedGuardDialog } = useUnsavedChangesGuard(
    isDirty,
    submitting,
  );

  function handleCreateNewUser() {
    const params = new URLSearchParams();
    if (email.trim()) params.set("prefillEmail", email.trim());
    if (eventId) params.set("prefillEventId", eventId);
    navigate(`/users/new?${params.toString()}`);
  }

  // Optional `e` lets this assign to FormPageLayout's `onSubmit: () => void`
  // contract while still calling preventDefault when the underlying <form>
  // dispatches its native submit event.
  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    setError(null);

    if (!eventId) return;
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Email is required.");
      return;
    }
    if (rosterStatus) {
      setError(
        `${match?.displayName ?? trimmedEmail} is already on this roster${
          rosterStatus === "pending" ? " with a pending invitation" : ""
        }. Send or resend their invitation email from the Attendees page.`,
      );
      return;
    }
    if (matchType === "new") {
      setError(
        'That email isn\'t in the system. Use "Create new user" below to add them first.',
      );
      return;
    }

    setSubmittingFlag(true);
    try {
      const result = await invite({ email: trimmedEmail, eventId });
      const name = match?.displayName || trimmedEmail;
      if (result.alreadyMember) {
        setError(`${name} is already a member of this event.`);
        setSubmittingFlag(false);
        return;
      }
      showSuccess(`${name} added to ${event?.name ?? "the event"}.`);
      navigate(`/events/${eventId}/attendees`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Add failed.";
      setError(msg);
      showError(msg);
      setSubmittingFlag(false);
    }
  }

  return (
    <>
      {unsavedGuardDialog}
      <FormPageLayout
        title={event ? `Add Attendee to ${event.name}` : "Add Attendee"}
        onSubmit={handleSubmit}
        error={error}
        submitting={submitting}
        submitLabel="Add Attendee"
        testIdSlug="attendee"
      >
        <FormSection title="Who">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Pick from existing users or contacts. They'll be added to the
            event roster. No email is sent on add: send invitation emails
            from the Attendees page Send Emails button when you're ready.
          </Typography>

          <Autocomplete
            freeSolo
            options={emailOptions}
            getOptionLabel={(opt) =>
              typeof opt === "string" ? opt : opt.email
            }
            filterOptions={(options, state) => {
              if (!state.inputValue) return options;
              const q = state.inputValue.toLowerCase();
              return options.filter(
                (o) =>
                  o.email.toLowerCase().includes(q) ||
                  o.displayName.toLowerCase().includes(q),
              );
            }}
            renderOption={(props, opt) => (
              <li {...props} key={`${opt.type}-${opt.id}`}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar
                    src={opt.photoURL ?? undefined}
                    sx={{ width: 28, height: 28 }}
                  >
                    {opt.displayName[0] || "?"}
                  </Avatar>
                  <Box>
                    <Box sx={{ fontWeight: 500 }}>{opt.displayName}</Box>
                    <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                      {opt.email}
                    </Box>
                  </Box>
                  <Chip
                    label={opt.type === "user" ? "Active" : "Contact"}
                    size="small"
                    variant="outlined"
                    color={opt.type === "user" ? "success" : "default"}
                    sx={{ ml: "auto" }}
                  />
                </Stack>
              </li>
            )}
            inputValue={email}
            onInputChange={(_, value) => setEmail(value)}
            onChange={(_, value) => {
              if (value && typeof value !== "string") setEmail(value.email);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Email"
                type="email"
                required
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    "data-testid": "attendee-create-email-input",
                  },
                }}
              />
            )}
          />

          {match && rosterStatus && (
            <Alert
              severity="info"
              sx={{ mt: 2 }}
              data-testid="attendee-create-on-roster-info"
            >
              <strong>{match.displayName}</strong> is already on this roster
              {rosterStatus === "pending" ? " with a pending invitation" : ""}.
              Send or resend their invitation email from the{" "}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate(`/events/${eventId}/attendees`)}
              >
                Attendees page
              </Link>
              .
            </Alert>
          )}

          {match && !rosterStatus && (
            <Alert
              severity="info"
              sx={{ mt: 2 }}
              data-testid="attendee-create-match-preview"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                  src={match.photoURL ?? undefined}
                  sx={{ width: 24, height: 24 }}
                >
                  {match.displayName[0]}
                </Avatar>
                <Box>
                  <strong>{match.displayName}</strong>
                  {", "}
                  {match.type === "user" ? "Active user" : "Contact"}
                </Box>
              </Stack>
            </Alert>
          )}

          {matchType === "new" && email.trim() && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              data-testid="attendee-create-no-match-warning"
            >
              That email isn't in the system yet. They need a user record
              before they can be added to the event.
            </Alert>
          )}

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Can't find them?{" "}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handleCreateNewUser}
                sx={{ fontWeight: 500 }}
                data-testid="attendee-create-new-user-link"
              >
                Create new user
              </Link>
            </Typography>
          </Box>
        </FormSection>
      </FormPageLayout>
    </>
  );
}
